import React, { useEffect, useState } from "react";

import { useDispatch } from "react-redux";

import { Label, CollapsibleCard } from "@busbud/horizon";

import { useAppContext } from "@app/helpers/hooks";
import type { UserBillingAddress } from "@app/types/napi-types";

import { BillingAddressForm } from "./billing-address-form";
import { EmptyBillingAddress } from "./empty-billing-address";
import { openModal } from "../../../../../store/state/modals";
import { addAddressClicked } from "../../../../../tracking/account-tracking";
import { deleteUserBillingAddressState } from "../../../state/user-billing-address";

export interface OwnProps {
  onError: (error: string) => void;
  onSuccess: () => void;
}

interface ReduxProps {
  user_billing_address: UserBillingAddress | null;
  user_id: number;
}

export const BillingAddressSection: React.FC<OwnProps & ReduxProps> = ({
  user_billing_address,
  user_id,
  onSuccess,
  onError
}) => {
  const { api, tracker, translator } = useAppContext();
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(false);
  const is_new_billing_address =
    user_billing_address === null ||
    user_billing_address?.street_line === undefined;
  const [address_form_shown, showAddressForm] =
    useState(!!user_billing_address);
  useEffect(() => {
    if (user_billing_address === null) {
      showAddressForm(false);
    }
  }, [user_billing_address]);

  if (!address_form_shown) {
    return (
      <EmptyBillingAddress
        onAddAddressClick={() => {
          tracker.track(addAddressClicked());
          showAddressForm(true);
          setExpanded(true);
        }}
      />
    );
  }

  const askDeleteConfirmation = () => {
    dispatch(
      openModal({
        type: "delete_billing_address",
        onConfirm: () => deleteBillingAddress()
      })
    );
  };

  const deleteBillingAddress = async () => {
    try {
      await api.deleteUserBillingAddress(user_id);
      dispatch(deleteUserBillingAddressState());
      onSuccess();
    } catch (err) {
      onError(translator.t("!accounts.settings.delete-billing-address-error"));
    }
  };

  return (
    <div className="flex flex-col gap-200">
      <Label as="span" size="lg" fontWeight="bold">
        {translator.t("!accounts.settings.billing-information")}
      </Label>
      <CollapsibleCard
        as="section"
        isInitiallyExpanded={expanded}
        withTransition
        renderHeader={headerProps => (
          <CollapsibleCard.Header
            {...headerProps}
            buttonAriaLabel={translator.t("!accounts.settings.expand-content")}
          >
            <CollapsibleCard.Title as="h2">
              {is_new_billing_address
                ? translator.t("!accounts.settings.new-billing-address")
                : user_billing_address!.street_line}
            </CollapsibleCard.Title>
          </CollapsibleCard.Header>
        )}
      >
        <CollapsibleCard.Body className="flex flex-col gap-150">
          <Label
            as="span"
            size="xs"
            fontWeight="bold"
            className="text-color-secondary"
          >
            {is_new_billing_address
              ? translator.t("!accounts.settings.add-billing-address")
              : translator.t("!accounts.settings.edit-billing-address")}
          </Label>
          <BillingAddressForm
            onSuccess={() => onSuccess()}
            onError={error => onError(error)}
            onDelete={() => askDeleteConfirmation()}
          />
        </CollapsibleCard.Body>
      </CollapsibleCard>
    </div>
  );
};
